/* eslint-disable react/no-unescaped-entities */
/* CONFILEX — Legal contents
 * Each export is rendered inside Legal.jsx tabs.
 * Designed to be split into separate pages later (CGU / CGV / Privacy / Cookies / Charte).
 */

import React from "react";
import { DefinitionsContent } from "./legal/definitions.jsx";

// Métadonnées de version des documents légaux (point 47 du cahier des charges).
// Format : { version, updated } — la date est en français (JJ mois AAAA).
const LEGAL_VERSIONS = {
  mentions: { version: "1.2", updated: "10 février 2026" },
  definitions: { version: "1.0", updated: "10 février 2026" },
  cgu: { version: "2.0", updated: "10 février 2026" },
  cgv: { version: "2.0", updated: "10 février 2026" },
  privacy: { version: "1.5", updated: "10 février 2026" },
  cookies: { version: "1.1", updated: "10 février 2026" },
  charte: { version: "1.0", updated: "10 février 2026" },
};

const VersionBadge = ({ docKey }) => {
  const meta = LEGAL_VERSIONS[docKey];
  if (!meta) return null;
  return (
    <div
      className="mb-6 flex flex-wrap items-center gap-3 text-xs text-slate-500"
      data-testid={`legal-version-${docKey}`}
    >
      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1">
        <span className="font-medium text-slate-700">Version</span> {meta.version}
      </span>
      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1">
        <span className="font-medium text-slate-700">Mise à jour</span> {meta.updated}
      </span>
    </div>
  );
};

const Article = ({ n, title, children }) => (
  <section className="mb-8">
    <h3 className="font-serif text-xl text-[#0B3A2A] mb-3">
      <span className="text-[#C9A961] mr-2">Article {n}</span>— {title}
    </h3>
    <div className="text-slate-700 leading-relaxed space-y-3 text-[15px]">{children}</div>
  </section>
);

const SubSection = ({ title, children }) => (
  <div className="mb-4">
    <h4 className="font-medium text-[#0B3A2A] mb-1">{title}</h4>
    <div className="text-slate-700 leading-relaxed">{children}</div>
  </div>
);

const List = ({ items }) => (
  <ul className="list-disc pl-6 space-y-1 text-slate-700">
    {items.map((it) => <li key={it}>{it}</li>)}
  </ul>
);

/* -------------------------------------------------------------------------- */
/* MENTIONS LÉGALES                                                            */
/* -------------------------------------------------------------------------- */
export const MentionsContent = () => (
  <>
    <h2 className="font-serif text-2xl text-[#0B3A2A] mb-6">Mentions légales</h2>
    <VersionBadge docKey="mentions" />

    <SubSection title="Éditeur">
      <p>
        CONFILEX SAS — Plateforme professionnelle de mise en relation. Siège social : Paris, France.
        Capital social : 10 000 €. RCS Paris XXX XXX XXX. Numéro de TVA intracommunautaire : FR XX XXX XXX XXX.
        Directeur de la publication : Direction CONFILEX.
      </p>
    </SubSection>

    <SubSection title="Hébergement">
      <p>
        Infrastructure cloud sécurisée hébergée au sein de l'Union européenne, conforme au Règlement Général
        sur la Protection des Données (RGPD).
      </p>
    </SubSection>

    <SubSection title="Contact">
      <p>
        Toute correspondance peut être adressée à <strong>contact@confilex.com</strong>.
        Pour toute question relative aux données personnelles : <strong>dpo@confilex.com</strong>.
      </p>
    </SubSection>

    <SubSection title="Positionnement">
      <p className="italic text-slate-600">
        CONFILEX est une plateforme collaborative professionnelle de mise en relation. Elle n'exerce
        aucune activité de conseil juridique, fiscal, comptable, notarial, patrimonial, financier ou technique.
        Les prestations sont réalisées exclusivement par les Spécialistes référencés, sous leur propre responsabilité professionnelle.
      </p>
    </SubSection>
  </>
);

/* -------------------------------------------------------------------------- */
/* CGU — Utilisateurs                                                          */
/* -------------------------------------------------------------------------- */
export const CGUContent = () => (
  <>
    <h2 className="font-serif text-2xl text-[#0B3A2A] mb-6">Conditions Générales d'Utilisation</h2>
    <VersionBadge docKey="cgu" />
    <p className="text-slate-600 italic mb-8">
      Les présentes CGU s'adressent aux Utilisateurs de la plateforme CONFILEX. Une version dédiée aux Spécialistes
      référencés est précisée dans les Conditions Générales de Vente CONFILEX.
    </p>

    <Article n="1" title="Acceptation">
      <p>
        L'utilisation de la plateforme CONFILEX implique l'acceptation pleine et entière des présentes Conditions
        Générales d'Utilisation. CONFILEX se réserve le droit de les modifier à tout moment. Les modifications entrent
        en vigueur dès leur publication.
      </p>
    </Article>

    <Article n="2" title="Définitions">
      <p>
        Les termes en majuscule utilisés dans les présentes CGU sont définis dans la page dédiée
        <em> « Définitions »</em> du cadre contractuel de Confilex, laquelle fait partie intégrante des présentes.
        Sont notamment définis : Confilex, Utilisateur, Professionnel, Données officielles, Données déclaratives,
        Profil revendiqué, Mise en relation, Dossier, Analyse assistée par intelligence artificielle.
      </p>
    </Article>

    <Article n="3" title="Qualification et rôle de Confilex">
      <p>
        Confilex est <strong>une plateforme numérique</strong>, un <strong>prestataire technique</strong>, un
        <strong> outil de préparation et de structuration</strong>, un <strong>service de référencement et de recherche</strong>
        et un <strong>intermédiaire technique de mise en relation</strong>.
      </p>
      <p className="rounded-lg border-l-4 border-[#C9A961] bg-[#C9A961]/5 py-3 px-4">
        Confilex fournit des services techniques destinés à faciliter l'analyse, la préparation, l'organisation et
        l'orientation des dossiers. Confilex ne se substitue pas aux professionnels réglementés et ne délivre pas,
        en cette seule qualité, les prestations réservées par la loi à ces professionnels.
      </p>
      <p>
        Sauf indication contraire expresse, Confilex reste extérieure au contrat éventuellement conclu entre
        l'Utilisateur et le Professionnel sélectionné. Le Professionnel demeure seul responsable de l'acceptation du
        dossier, de la définition de sa mission, de ses conseils, de sa stratégie, de ses diligences et de ses honoraires.
      </p>
    </Article>

    <Article n="4" title="Rôle strictement technique">
      <p>CONFILEX agit exclusivement comme intermédiaire technique. CONFILEX ne peut être considérée comme :</p>
      <List items={[
        "un cabinet d'avocats, une étude notariale, un cabinet d'expertise comptable ou de conseil",
        "un mandataire ou un courtier en prestations réglementées",
        "un apporteur d'affaires rémunéré sur honoraires réglementés",
        "une partie au contrat conclu entre l'Utilisateur et le Spécialiste",
      ]} />
      <p>
        CONFILEX ne garantit ni la qualité finale de la prestation, ni son résultat, ni l'issue d'un dossier,
        ni la satisfaction de l'Utilisateur.
      </p>
    </Article>

    <Article n="5" title="Création de compte">
      <p>
        L'Utilisateur s'engage à fournir des informations exactes et à jour. Chaque compte est strictement
        personnel et les identifiants de connexion doivent être conservés confidentiellement.
      </p>
    </Article>

    <Article n="6" title="Libre choix du Professionnel">
      <p className="rounded-lg border-l-4 border-[#C9A961] bg-[#C9A961]/5 py-3 px-4">
        L'Utilisateur conserve à tout moment le libre choix du Professionnel auquel il souhaite s'adresser. Les
        résultats affichés par Confilex constituent des propositions de correspondance établies à partir de critères
        objectifs et ne constituent ni une désignation imposée, ni une garantie de résultat, ni une appréciation
        définitive des compétences du Professionnel pour le dossier concerné.
      </p>
      <p>
        Lorsque plusieurs résultats pertinents existent, Confilex en présente plusieurs. Aucun Professionnel n'est
        attribué automatiquement sans action claire de l'Utilisateur. Aucun Dossier complet n'est transmis
        automatiquement avant que l'Utilisateur ait confirmé le destinataire et le périmètre de la transmission.
      </p>
    </Article>

    <Article n="7" title="Absence de relation avocat-client avec Confilex">
      <p className="rounded-lg border-l-4 border-[#C9A961] bg-[#C9A961]/5 py-3 px-4">
        L'utilisation de Confilex ne crée pas, à elle seule, une relation entre l'Utilisateur et un avocat ou un
        autre professionnel réglementé. Cette relation ne naît qu'après l'acceptation de la mission par le
        Professionnel concerné et, lorsqu'elle est requise, la conclusion de sa propre convention d'intervention
        ou convention d'honoraires.
      </p>
      <p>
        L'analyse automatisée, la préparation d'un dossier ou la transmission d'une demande <strong>ne valent pas
        acceptation du dossier</strong> par le Professionnel.
      </p>
    </Article>

    <Article n="8" title="Analyse documentaire assistée par intelligence artificielle">
      <p>Confilex peut notamment :</p>
      <List items={[
        "extraire des informations",
        "organiser des pièces",
        "produire une chronologie",
        "détecter des incohérences",
        "identifier des clauses ou mentions",
        "signaler des points de vigilance",
        "proposer une qualification initiale",
        "préparer une synthèse",
        "suggérer des questions à approfondir",
      ]} />
      <p className="rounded-lg border-l-4 border-amber-500 bg-amber-50 py-3 px-4">
        Les résultats générés automatiquement peuvent comporter des erreurs, omissions ou interprétations incomplètes.
        Ils doivent être vérifiés au regard des documents originaux, des circonstances du dossier et, lorsque
        nécessaire, par un professionnel compétent.
      </p>
      <p>
        Confilex ne garantit pas l'exhaustivité de la détection des erreurs, clauses, délais, risques ou irrégularités.
        L'Utilisateur ne doit pas se fonder exclusivement sur une analyse automatisée lorsqu'une décision peut avoir
        des conséquences juridiques, financières, fiscales, sociales, patrimoniales ou procédurales importantes.
      </p>
      <p>
        Confilex n'est pas présentée comme un avocat, un notaire, un expert-comptable, un professionnel réglementé,
        une autorité publique ou une juridiction.
      </p>
    </Article>

    <Article n="9" title="Délais et prescriptions">
      <p className="rounded-lg border-l-4 border-amber-500 bg-amber-50 py-3 px-4">
        Les indications relatives aux dates, échéances, prescriptions, forclusions, délais de recours ou délais
        procéduraux sont fournies à titre d'assistance et dépendent des documents, faits et informations transmis
        par l'Utilisateur.
      </p>
      <p>
        La détermination d'un délai peut dépendre de circonstances non connues de Confilex, notamment la date de
        notification, la nature d'un acte, une interruption, une suspension, une prorogation ou une règle spéciale.
      </p>
      <p>
        <strong>L'Utilisateur doit faire vérifier sans délai</strong> toute échéance susceptible d'avoir une incidence
        sur l'exercice d'un droit ou d'une action. Lorsque le système détecte une échéance potentielle, une alerte
        visible est affichée avec la mention : « Échéance à vérifier — cette date ne doit pas être considérée
        comme définitivement validée sans contrôle des pièces et du régime juridique applicable ».
      </p>
    </Article>

    <Article n="10" title="Comportement attendu">
      <p>
        L'Utilisateur s'engage à utiliser la plateforme de manière loyale, à ne pas porter atteinte aux droits des
        tiers et à respecter la confidentialité des échanges.
      </p>
    </Article>

    <Article n="11" title="Données personnelles">
      <p>
        Le traitement des données personnelles est précisé dans la Politique de confidentialité. L'Utilisateur
        dispose à tout moment des droits d'accès, de rectification, d'effacement et de portabilité prévus par le RGPD.
      </p>
    </Article>

    <Article n="12" title="Droit applicable">
      <p>
        Les présentes CGU sont soumises au droit français. Tout litige relatif à leur application sera, à défaut
        d'accord amiable, soumis aux juridictions françaises compétentes.
      </p>
    </Article>
  </>
);

/* -------------------------------------------------------------------------- */
/* CGV — Spécialistes (texte fourni par CONFILEX)                              */
/* -------------------------------------------------------------------------- */
export const CGVContent = () => (
  <>
    <h2 className="font-serif text-2xl text-[#0B3A2A] mb-3">Conditions Générales de Vente</h2>
    <VersionBadge docKey="cgv" />
    <p className="text-slate-600 italic mb-8">
      Les présentes CGV s'appliquent aux Spécialistes référencés sur CONFILEX et, lorsque cela est précisé, aux Utilisateurs.
    </p>

    <section className="mb-8">
      <h3 className="font-serif text-xl text-[#0B3A2A] mb-3">Préambule</h3>
      <div className="text-slate-700 leading-relaxed space-y-3 text-[15px]">
        <p>
          CONFILEX est une plateforme collaborative professionnelle ayant pour objet de faciliter la mise en
          relation entre des Utilisateurs et des Spécialistes référencés.
        </p>
        <p>
          CONFILEX intervient exclusivement comme opérateur technique de mise en relation, d'hébergement de profils,
          de visibilité professionnelle et d'outils numériques.
        </p>
        <p>
          CONFILEX ne fournit aucune consultation juridique, fiscale, comptable, notariale, patrimoniale, financière
          ou technique. Les prestations sont réalisées exclusivement par les Spécialistes, sous leur seule
          responsabilité professionnelle.
        </p>
      </div>
    </section>

    <Article n="1" title="Définitions">
      <SubSection title="CONFILEX">Désigne la société exploitant la plateforme numérique CONFILEX.</SubSection>
      <SubSection title="Utilisateur">
        Désigne toute personne physique ou morale utilisant la plateforme afin de rechercher, consulter ou contacter un Spécialiste.
      </SubSection>
      <SubSection title="Spécialiste">
        Désigne tout professionnel référencé sur CONFILEX : avocat, notaire, expert-comptable, commissaire de justice,
        consultant, spécialiste patrimonial, immobilier, financier, fiscal ou technique.
      </SubSection>
      <SubSection title="Plateforme">
        Désigne le site internet, les espaces personnels, les outils de mise en relation, de messagerie, de paiement,
        de publication, d'analyse et d'orientation proposés par CONFILEX.
      </SubSection>
      <SubSection title="Services CONFILEX">
        Désignent les services techniques fournis par CONFILEX : référencement, création de profil, visibilité,
        hébergement de contenus, outils collaboratifs, espace documentaire, outils d'aide à l'orientation, services
        premium et communication institutionnelle.
      </SubSection>
    </Article>

    <Article n="2" title="Objet">
      <p>Les présentes CGV définissent les conditions dans lesquelles CONFILEX fournit ses services aux Spécialistes et, le cas échéant, aux Utilisateurs. CONFILEX permet notamment :</p>
      <List items={[
        "la création de profils professionnels",
        "le référencement de Spécialistes",
        "la mise en relation avec des Utilisateurs",
        "la publication de contenus",
        "l'accès à des outils collaboratifs",
        "l'utilisation d'outils d'orientation ou de synthèse assistés",
        "la souscription à des options de visibilité ou services premium",
      ]} />
      <p>CONFILEX n'est jamais partie au contrat conclu entre l'Utilisateur et le Spécialiste.</p>
    </Article>

    <Article n="3" title="Rôle strictement technique de CONFILEX">
      <p>CONFILEX agit exclusivement comme plateforme collaborative et intermédiaire technique. CONFILEX ne peut en aucun cas être considérée comme :</p>
      <List items={[
        "un cabinet d'avocats",
        "une étude notariale",
        "un cabinet d'expertise comptable",
        "un cabinet de conseil",
        "un mandataire",
        "un courtier en prestations réglementées",
        "un apporteur d'affaires rémunéré sur honoraires réglementés",
        "une partie au contrat conclu entre l'Utilisateur et le Spécialiste",
      ]} />
      <p>CONFILEX ne garantit ni la qualité finale de la prestation, ni son résultat, ni l'issue d'un dossier, ni la satisfaction de l'Utilisateur.</p>
    </Article>

    <Article n="4" title="Indépendance des Spécialistes">
      <p>Les Spécialistes référencés exercent en toute indépendance. Ils sont seuls responsables :</p>
      <List items={[
        "de leurs titres professionnels",
        "de leur inscription ordinale ou réglementaire",
        "de leurs déclarations",
        "de leurs contenus",
        "de leurs honoraires",
        "de leurs conseils",
        "de leurs actes",
        "de leurs prestations",
        "de leur relation contractuelle avec les Utilisateurs",
        "du respect de leurs obligations professionnelles, légales et déontologiques",
      ]} />
      <p>Aucun lien de subordination, mandat, société créée de fait, association, représentation ou partenariat juridique ne peut être déduit de leur présence sur CONFILEX.</p>
    </Article>

    <Article n="5" title="Relation entre Utilisateur et Spécialiste">
      <p>L'Utilisateur choisit librement le Spécialiste avec lequel il souhaite entrer en relation. Le Spécialiste reste libre :</p>
      <List items={[
        "d'accepter ou de refuser une demande",
        "de fixer ses conditions d'intervention",
        "de fixer ses honoraires",
        "de demander des pièces complémentaires",
        "de conclure ou non une convention avec l'Utilisateur",
      ]} />
      <p>Toute convention, lettre de mission, convention d'honoraires, devis, facture ou accord contractuel est conclu directement entre l'Utilisateur et le Spécialiste. CONFILEX n'intervient pas dans la négociation, l'exécution ou le règlement des différends entre eux.</p>
    </Article>

    <Article n="6" title="Absence de responsabilité de CONFILEX">
      <p>CONFILEX ne saurait être responsable :</p>
      <List items={[
        "du choix d'un Spécialiste par un Utilisateur",
        "du refus d'un Spécialiste d'intervenir",
        "des conseils donnés",
        "des actes réalisés",
        "des honoraires demandés",
        "d'une erreur professionnelle",
        "d'un retard",
        "d'un conflit d'intérêts",
        "d'un différend entre Utilisateur et Spécialiste",
        "d'un résultat défavorable",
        "d'une perte financière",
        "d'une perte de chance",
        "d'une faute commise par un Spécialiste",
        "d'un contenu publié par un Spécialiste ou un Utilisateur",
      ]} />
      <p>CONFILEX fournit uniquement un environnement numérique de mise en relation et de services techniques.</p>
      <p className="rounded-lg border-l-4 border-emerald-500 bg-emerald-50 py-3 px-4">
        Confilex s'engage cependant à mettre en œuvre les moyens raisonnables nécessaires au fonctionnement,
        à la sécurité et à la loyauté de ses propres services.
      </p>
    </Article>

    <Article n="7" title="Abonnements des Spécialistes">
      <p>Les Spécialistes peuvent souscrire différents services :</p>
      <List items={[
        "référencement standard",
        "profil premium",
        "visibilité renforcée",
        "publication d'articles",
        "contenus audio ou vidéo",
        "espace documentaire",
        "outils d'analyse documentaire",
        "services de communication",
        "services de présentation institutionnelle",
      ]} />
      <p>Les conditions tarifaires sont précisées lors de la souscription. L'abonnement peut être mensuel, annuel ou selon toute autre formule proposée par CONFILEX.</p>
    </Article>

    <Article n="8" title="Prix et paiement">
      <p>Les prix sont exprimés en euros hors taxes ou toutes taxes comprises selon les indications affichées. Le paiement peut être effectué par carte bancaire, prélèvement, virement ou toute solution de paiement sécurisée proposée par CONFILEX.</p>
      <p>Tout retard de paiement peut entraîner :</p>
      <List items={[
        "la suspension du profil",
        "la limitation des services",
        "la suppression de la visibilité",
        "la résiliation de l'abonnement",
        "l'exigibilité des sommes restant dues",
      ]} />
      <p>CONFILEX peut appliquer les pénalités de retard et indemnités de recouvrement prévues par la réglementation applicable aux professionnels.</p>
    </Article>

    <Article n="8 bis" title="Rapport d'audit préparatoire à 89 € TTC (Utilisateurs consommateurs)">
      <p>L'Utilisateur consommateur peut commander un Rapport d'audit préparatoire pour un prix forfaitaire unitaire de <strong>89,00 € TTC</strong> (74,17 € HT + 14,83 € de TVA française au taux de 20 %). Le paiement intervient à la commande via le prestataire Stripe.</p>
      <SubSection title="Nature de la prestation">
        Le Rapport est un contenu numérique constitué d'une synthèse documentaire automatisée et d'un PDF téléchargeable. Il <strong>ne constitue ni une consultation juridique, ni un conseil fiscal, comptable ou financier</strong>, ni une garantie de résultat. Il est destiné à préparer un dossier avant rencontre éventuelle avec un professionnel compétent.
      </SubSection>
      <SubSection title="Droit de rétractation de 14 jours">
        Conformément aux articles L221-18 et suivants du Code de la consommation, l'Utilisateur consommateur dispose d'un délai de quatorze (14) jours pour exercer son droit de rétractation, sans avoir à se justifier.
      </SubSection>
      <SubSection title="Renonciation expresse au droit de rétractation">
        Le Rapport étant un contenu numérique fourni sur un support immatériel et dont l'exécution commence immédiatement après le paiement, l'Utilisateur reconnaît, en cochant la case prévue à cet effet avant le paiement, qu'il <strong>demande expressément la fourniture immédiate du Rapport</strong> et qu'il <strong>renonce expressément à son droit de rétractation</strong>, en application de l'article L221-28 1° du Code de la consommation. Une fois le Rapport généré et mis à disposition, il ne pourra donc faire l'objet d'aucun remboursement à ce titre.
      </SubSection>
      <SubSection title="Hypothèses de remboursement">
        Un remboursement intégral peut être demandé par email à <strong>support@confilex.com</strong> dans les sept (7) jours suivant le paiement uniquement si : (i) le Rapport n'a jamais été généré pour cause d'incident technique imputable à CONFILEX, (ii) le Rapport est manifestement vide ou inexploitable, (iii) toute autre situation où la prestation n'a pas été délivrée.
      </SubSection>
      <SubSection title="Facturation et TVA">
        Une facture mentionnant le HT, la TVA et le TTC est émise par CONFILEX et adressée à l'email indiqué lors de la commande. CONFILEX est immatriculée en France et collecte la TVA française.
      </SubSection>
      <SubSection title="Médiateur de la consommation">
        Conformément à l'article L612-1 du Code de la consommation, l'Utilisateur consommateur peut recourir gratuitement au médiateur de la consommation référencé par CONFILEX. Les coordonnées du médiateur sont précisées dans les Mentions légales.
      </SubSection>
    </Article>

    <Article n="9" title="Professions réglementées">
      <p>Lorsque le Spécialiste exerce une profession réglementée, il lui appartient de vérifier que son utilisation de CONFILEX est conforme à ses règles professionnelles et déontologiques.</p>
      <p>CONFILEX ne perçoit aucune commission sur les honoraires réglementés lorsque cette rémunération serait interdite par les règles applicables. Les sommes versées à CONFILEX rémunèrent uniquement :</p>
      <List items={[
        "l'accès à la plateforme",
        "le référencement",
        "l'hébergement du profil",
        "la visibilité",
        "les outils numériques",
        "les services éditoriaux",
        "les services techniques",
        "les options premium",
      ]} />
    </Article>

    <Article n="10" title="Inscription et validation des Spécialistes">
      <p>CONFILEX peut demander tout justificatif utile avant ou après inscription : identité, diplôme, titre professionnel, numéro ordinal, assurance professionnelle, SIRET, références, domaines d'intervention.</p>
      <p>CONFILEX se réserve le droit de refuser, suspendre ou déréférencer tout profil ne correspondant pas à ses critères de qualité, de conformité ou d'image.</p>
    </Article>

    <Article n="11" title="Contenus publiés">
      <p>Le Spécialiste reste seul responsable des contenus publiés sur son profil. Il garantit que ses contenus :</p>
      <List items={[
        "sont exacts",
        "ne sont pas trompeurs",
        "respectent la loi",
        "respectent les droits des tiers",
        "respectent les règles déontologiques applicables",
      ]} />
      <p>CONFILEX peut retirer tout contenu illicite, inexact, trompeur, inapproprié ou contraire à l'image de la plateforme.</p>
    </Article>

    <Article n="12" title="Outils d'analyse documentaire">
      <p>Les outils d'analyse documentaire proposés par CONFILEX ont pour objet d'aider à :</p>
      <List items={[
        "structurer une demande",
        "orienter un utilisateur",
        "synthétiser des informations",
        "identifier des thématiques",
        "améliorer la compréhension générale d'un besoin",
      ]} />
      <p>Ces outils ne constituent jamais une consultation juridique, fiscale, comptable, notariale, financière ou technique. Toute information générée doit être vérifiée par un professionnel compétent.</p>
      <p>CONFILEX ne garantit ni l'exactitude, ni l'exhaustivité, ni l'adéquation des résultats produits.</p>
    </Article>

    <Article n="13" title="Données et confidentialité">
      <p>CONFILEX traite les données personnelles conformément à sa Politique de confidentialité. Les données peuvent être utilisées pour :</p>
      <List items={[
        "créer les comptes",
        "gérer les profils",
        "assurer la mise en relation",
        "sécuriser les échanges",
        "gérer la facturation",
        "améliorer les services",
        "faire fonctionner les outils numériques, dans le respect du RGPD",
      ]} />
      <p>Les Utilisateurs et Spécialistes demeurent responsables des documents et informations qu'ils transmettent via la plateforme.</p>
    </Article>

    <Article n="14" title="Suspension et déréférencement">
      <p>CONFILEX peut suspendre ou supprimer un compte en cas de :</p>
      <List items={[
        "non-paiement",
        "fausse déclaration",
        "comportement illicite",
        "contenu trompeur",
        "signalement sérieux",
        "perte du titre professionnel",
        "non-respect des présentes CGV",
        "atteinte à l'image ou au bon fonctionnement de CONFILEX",
      ]} />
      <p>Cette suspension peut intervenir sans préavis en cas d'urgence, de risque juridique, de risque réputationnel ou de manquement grave.</p>
    </Article>

    <Article n="15" title="Résiliation">
      <p>Le Spécialiste peut résilier son abonnement selon les conditions prévues dans l'offre souscrite. Sauf stipulation contraire, les sommes déjà versées restent acquises à CONFILEX.</p>
      <p>En cas d'abonnement annuel avec paiement mensuel, les mensualités restantes peuvent demeurer dues jusqu'au terme de la période d'engagement.</p>
    </Article>

    <Article n="16" title="Force majeure">
      <p>CONFILEX ne saurait être responsable en cas d'impossibilité d'exécution liée à un événement extérieur, imprévisible ou irrésistible, notamment :</p>
      <List items={[
        "panne technique",
        "cyberattaque",
        "défaillance d'hébergeur",
        "interruption internet",
        "événement administratif",
        "catastrophe naturelle",
        "décision d'autorité publique",
      ]} />
    </Article>

    <Article n="17" title="Propriété intellectuelle et propriété des documents Client">
      <p>La plateforme CONFILEX, son nom, son identité visuelle, ses contenus, ses interfaces, ses bases de données, ses outils et ses développements sont protégés par le droit de la propriété intellectuelle.</p>
      <p>Toute reproduction, extraction, copie ou utilisation non autorisée est interdite.</p>
      <p>Les contenus transmis par les Spécialistes restent leur propriété, sous réserve de la licence d'utilisation accordée à CONFILEX pour les besoins de leur diffusion sur la plateforme.</p>
      <p><strong>Les documents déposés par le Client (notamment via le Vérificateur) demeurent la propriété exclusive de leur auteur ou de leur titulaire de droits.</strong> CONFILEX ne revendique aucun droit sur ces documents. Ces documents ne sont jamais revendus, jamais cédés à des tiers, jamais utilisés à des fins publicitaires, et jamais utilisés pour entraîner des modèles d'intelligence artificielle sans consentement exprès et préalable du Client.</p>
    </Article>

    <Article n="18" title="Positionnement de CONFILEX — absence de qualité de cabinet réglementé">
      <p>CONFILEX <strong>n'est pas</strong> un cabinet d'avocats, ni une étude notariale, ni un cabinet d'expertise comptable, ni un cabinet de conseil fiscal, financier ou patrimonial. CONFILEX est une plateforme technologique permettant le dépôt sécurisé, le traitement automatisé, le classement, la synthèse factuelle et la transmission éventuelle de documents à un professionnel qualifié.</p>
      <p>Les analyses produites par CONFILEX constituent uniquement des traitements automatisés d'informations et <strong>ne remplacent pas l'intervention d'un professionnel qualifié</strong>.</p>
      <p>CONFILEX ne <strong>bénéficie pas du secret professionnel</strong> attaché aux professions réglementées. Le secret professionnel demeure attaché au professionnel lui-même, dans les conditions définies par les textes et règles déontologiques propres à sa profession (notamment l'article 66-5 de la loi du 31 décembre 1971 pour les avocats, l'article 23 de l'ordonnance du 19 septembre 1945 pour les experts-comptables, l'article 3-1 de l'ordonnance du 2 novembre 1945 pour les notaires).</p>
    </Article>

    <Article n="19" title="Conformité déontologique et indépendance des Spécialistes">
      <p>CONFILEX respecte strictement les principes suivants applicables aux professions réglementées :</p>
      <ul className="list-disc pl-6 my-3 space-y-1">
        <li><strong>Indépendance</strong> des Spécialistes référencés dans l'exercice de leur profession.</li>
        <li><strong>Absence de démarchage interdit</strong> — CONFILEX ne sollicite pas le Client pour le compte d'un Spécialiste déterminé.</li>
        <li><strong>Absence de partage d'honoraires</strong> — l'abonnement Spécialiste constitue un référencement de visibilité sur la plateforme, indépendant du chiffre d'affaires généré par le Spécialiste auprès du Client.</li>
        <li><strong>Transparence des mises en relation</strong> — chaque Client est informé du caractère de marketplace de la plateforme.</li>
        <li><strong>Libre choix du professionnel</strong> par le Client — CONFILEX peut suggérer mais n'impose jamais un Spécialiste.</li>
      </ul>
    </Article>

    <Article n="20" title="Litiges">
      <p>Les présentes CGV sont soumises au droit français. Tout litige relatif aux présentes conditions sera, à défaut d'accord amiable, soumis aux juridictions françaises compétentes.</p>
    </Article>
  </>
);

/* -------------------------------------------------------------------------- */
/* CONFIDENTIALITÉ                                                             */
/* -------------------------------------------------------------------------- */
export const PrivacyContent = () => (
  <>
    <h2 className="font-serif text-2xl text-[#0B3A2A] mb-6">Politique de confidentialité</h2>
    <VersionBadge docKey="privacy" />

    <SubSection title="Responsable du traitement">
      <p>CONFILEX SAS — contact : <strong>dpo@confilex.com</strong>.</p>
    </SubSection>

    <SubSection title="Données collectées">
      <p>CONFILEX collecte les données nécessaires à la fourniture du service :</p>
      <List items={[
        "données d'identification (nom, prénom, email, téléphone, photographie professionnelle)",
        "données de profil professionnel (titre, profession, cabinet, domaines d'intervention)",
        "données d'usage de la plateforme (connexions, échanges, transactions)",
        "données techniques nécessaires à la sécurité (adresse IP, journal d'activité)",
      ]} />
    </SubSection>

    <SubSection title="Finalités">
      <List items={[
        "création et gestion des comptes",
        "mise en relation entre Utilisateurs et Spécialistes référencés",
        "sécurisation des échanges et lutte contre la fraude",
        "facturation et gestion administrative",
        "amélioration des services et fonctionnement des outils d'analyse documentaire",
      ]} />
    </SubSection>

    <SubSection title="Bases légales">
      <p>Exécution du contrat (utilisation de la plateforme), intérêt légitime (sécurité, amélioration des services), consentement (le cas échéant) et obligations légales.</p>
    </SubSection>

    <SubSection title="Durée de conservation">
      <p>Les données sont conservées pendant la durée d'utilisation du compte, puis archivées conformément aux délais légaux applicables avant suppression définitive. Les durées indicatives appliquées sont les suivantes :</p>
      <List items={[
        "Comptes utilisateurs actifs : pendant toute la durée d'utilisation, puis 3 ans après le dernier contact (prospection commerciale).",
        "Documents déposés dans un workspace inactif : 180 jours après la dernière activité, puis suppression automatique.",
        "Transcriptions et enregistrements vocaux (Voice AI) : 90 jours.",
        "Logs d'audit administrateur et journaux de sécurité : 2 ans (Art. 32 RGPD).",
        "Données comptables et factures : 10 ans (Art. L123-22 Code de commerce).",
      ]} />
    </SubSection>

    <SubSection title="Droits des personnes">
      <p>Conformément au RGPD, vous disposez à tout moment des droits d'accès (Art. 15), de rectification (Art. 16), d'effacement (Art. 17), d'opposition (Art. 21), de limitation (Art. 18) et de portabilité (Art. 20) de vos données.</p>
      <p className="mt-2">Vous pouvez exercer ces droits :</p>
      <List items={[
        "depuis votre espace personnel : « Exporter mes données » (Art. 20) ou « Supprimer mon compte » (Art. 17),",
        "par email à <strong>dpo@confilex.com</strong> en justifiant de votre identité,",
        "par courrier à l'adresse du siège social.",
      ]} />
      <p className="mt-2">Vous disposez également du droit d'introduire une réclamation auprès de la CNIL (<strong>www.cnil.fr</strong>) en cas de désaccord persistant.</p>
    </SubSection>

    <SubSection title="Sous-traitants & transferts hors UE">
      <p>CONFILEX fait appel aux sous-traitants techniques suivants, sélectionnés pour leurs garanties de sécurité et de conformité RGPD. Chacun est encadré par un accord de traitement des données (DPA) et, le cas échéant, par des clauses contractuelles types CE pour les transferts hors UE :</p>
      <List items={[
        "Stripe Payments Europe (IE) — paiements (transferts US sous SCCs).",
        "Twilio Inc. (US) — SMS et téléphonie (SCCs + chiffrement TLS).",
        "Vapi AI Inc. (US) — assistant vocal Charlotte (SCCs + minimisation transcripts).",
        "ElevenLabs Inc. (US) — synthèse vocale (SCCs, données vocales transitoires).",
        "Anthropic PBC (US) — modèle Claude pour la synthèse documentaire (SCCs, no-training opt-out).",
        "OpenAI Ireland Ltd (IE) — modèles GPT pour les agents auxiliaires (SCCs, no-training).",
        "Resend Inc. (US) — emails transactionnels (SCCs).",
        "Sentry / Functional Software Inc. (US) — supervision technique (SCCs, scrubbing PII).",
        "LinkedIn Ireland Unlimited Company (IE) — diffusion de contenus institutionnels.",
        "Hébergement : opérateur conforme RGPD, datacenters européens.",
      ]} />
      <p className="mt-2">Aucune donnée n'est cédée à des tiers commerciaux. Une analyse d'impact relative à la protection des données (AIPD) est tenue à jour pour les traitements à risque.</p>
    </SubSection>

    <SubSection title="Suppression de compte (Art. 17)">
      <p>Vous pouvez supprimer votre compte à tout moment depuis votre espace personnel. La suppression est immédiate côté plateforme : vos données d'identification sont pseudonymisées et votre session est invalidée. Les pièces comptables sont conservées 10 ans conformément à la loi française mais ne permettent plus de vous identifier directement.</p>
    </SubSection>
  </>
);

/* -------------------------------------------------------------------------- */
/* COOKIES                                                                     */
/* -------------------------------------------------------------------------- */
export const CookiesContent = () => (
  <>
    <h2 className="font-serif text-2xl text-[#0B3A2A] mb-6">Politique cookies</h2>
    <VersionBadge docKey="cookies" />

    <SubSection title="Cookies essentiels">
      <p>CONFILEX dépose uniquement les cookies strictement nécessaires au fonctionnement de la plateforme : maintien de session, sécurité de l'authentification, protection contre les attaques automatisées.</p>
    </SubSection>

    <SubSection title="Cookies de mesure d'audience">
      <p>Le cas échéant, CONFILEX peut utiliser des cookies de mesure d'audience anonymisés afin d'améliorer l'expérience utilisateur. Aucune donnée n'est utilisée à des fins publicitaires.</p>
    </SubSection>

    <SubSection title="Cookies publicitaires">
      <p className="italic text-slate-600">Aucun cookie publicitaire ou de tracking comportemental n'est déposé par CONFILEX.</p>
    </SubSection>

    <SubSection title="Paramétrage">
      <p>Vous pouvez à tout moment paramétrer les cookies depuis les réglages de votre navigateur. La désactivation des cookies essentiels peut empêcher le bon fonctionnement de certaines fonctionnalités de la plateforme.</p>
    </SubSection>
  </>
);

/* -------------------------------------------------------------------------- */
/* CHARTE QUALITÉ CONFILEX                                                     */
/* -------------------------------------------------------------------------- */
export const CharteContent = () => (
  <>
    <h2 className="font-serif text-2xl text-[#0B3A2A] mb-6">Charte qualité CONFILEX</h2>
    <VersionBadge docKey="charte" />
    <p className="text-slate-600 italic mb-8">
      CONFILEX s'engage à maintenir un environnement professionnel exigeant. La présente Charte rappelle
      les principes que la plateforme et les Spécialistes référencés s'engagent à respecter.
    </p>

    <SubSection title="Sélection et référencement">
      <p>Chaque candidature de Spécialiste fait l'objet d'une étude attentive. CONFILEX vérifie la cohérence des informations transmises, les domaines d'intervention déclarés et, lorsque cela est pertinent, les justificatifs professionnels.</p>
    </SubSection>

    <SubSection title="Qualité des profils">
      <p>Les profils référencés doivent présenter une information exacte, à jour et fidèle à l'activité réelle du Spécialiste. CONFILEX peut demander une mise à jour à tout moment.</p>
    </SubSection>

    <SubSection title="Confidentialité">
      <p>Les échanges, dossiers et documents partagés via la plateforme bénéficient d'un environnement sécurisé et confidentiel. Les Spécialistes s'engagent à respecter strictement la confidentialité des informations communiquées par les Utilisateurs.</p>
    </SubSection>

    <SubSection title="Déontologie">
      <p>Chaque Spécialiste exerçant une profession réglementée s'engage à respecter pleinement ses obligations professionnelles, légales et déontologiques. L'anonymisation des décisions et l'absence d'identification des parties dans les contenus publiés sont strictement requises.</p>
    </SubSection>

    <SubSection title="Tenue institutionnelle">
      <p>Les contenus, biographies, publications et présentations diffusés sur CONFILEX adoptent un registre sobre et professionnel, conforme à l'image d'un environnement de cabinet d'affaires.</p>
    </SubSection>

    <SubSection title="Engagement de CONFILEX">
      <p>CONFILEX s'engage à maintenir la plateforme à un niveau d'exigence élevé : fiabilité technique, sécurité des données, qualité des outils numériques mis à disposition et continuité du service.</p>
    </SubSection>
  </>
);

/* -------------------------------------------------------------------------- */
/* RÔLE DE CONFILEX — Information centrale sur le positionnement              */
/* -------------------------------------------------------------------------- */
export const RolePlateformeContent = () => (
  <>
    <h2 className="font-serif text-2xl text-[#0B3A2A] mb-6">Rôle exact de CONFILEX</h2>

    <div className="rounded-xl border-l-4 border-[#C9A961] bg-[#FAF7F1] p-5 mb-6">
      <p className="text-[#0B3A2A] font-medium">
        CONFILEX est une plateforme d'intermédiation technique et commerciale.
      </p>
      <p className="text-slate-700 mt-2 text-[15px]">
        CONFILEX n'est pas un cabinet d'avocats, une étude notariale, un cabinet d'expertise-comptable,
        un cabinet de conseil juridique, ni un organe ordinal ou institutionnel.
      </p>
    </div>

    <SubSection title="Ce que CONFILEX fait">
      <List items={[
        "Identifie automatiquement la discipline concernée à partir d'un récit utilisateur (référentiel multidisciplinaire, 154 spécialités).",
        "Analyse documentaire factuelle (vigilance, structuration, repérage de pièces clés).",
        "Présente jusqu'à 3 profils de spécialistes pertinents pour chaque demande, sélectionnés sur des critères factuels et vérifiables.",
        "Trace tous les accès aux dossiers via un journal d'audit opposable.",
        "Sécurise la communication entre l'utilisateur et le spécialiste choisi.",
      ]} />
    </SubSection>

    <SubSection title="Ce que CONFILEX ne fait jamais">
      <List items={[
        "Aucune consultation juridique, fiscale, comptable, notariale, patrimoniale ou financière directe.",
        "Aucune notation de la performance, du « taux de succès » ou de la réputation d'un professionnel.",
        "Aucune garantie de résultat sur une mission confiée à un spécialiste.",
        "Aucune ingérence dans la relation contractuelle nouée entre l'utilisateur et le spécialiste choisi.",
        "Aucun classement assimilable à un palmarès ordinal ou ordinalisable.",
      ]} />
    </SubSection>

    <SubSection title="Statut des analyses produites par CONFILEX">
      <p>
        Les analyses automatiques, vérifications documentaires ou orientations produites par CONFILEX
        ont une vocation strictement <strong>informative, préparatoire, documentaire et d'aide à l'orientation</strong>.
        Elles ne remplacent jamais l'intervention d'un professionnel réglementé compétent et ne sauraient
        engager la responsabilité civile ou professionnelle de CONFILEX au-delà de la fourniture du service numérique.
      </p>
    </SubSection>
  </>
);

/* -------------------------------------------------------------------------- */
/* MISE EN RELATION — Fonctionnement détaillé                                  */
/* -------------------------------------------------------------------------- */
export const MiseEnRelationContent = () => (
  <>
    <h2 className="font-serif text-2xl text-[#0B3A2A] mb-6">Fonctionnement de la mise en relation</h2>

    <Article n="1" title="Description du besoin par l'utilisateur">
      <p>
        L'utilisateur décrit librement sa situation via le moteur d'orientation (champ libre, recherche
        prédictive, arborescence guidée ou dictée vocale). CONFILEX qualifie automatiquement la discipline
        concernée à partir d'un référentiel multidisciplinaire à 4 niveaux (17 grands domaines, 154 spécialités).
      </p>
    </Article>

    <Article n="2" title="Proposition de profils par CONFILEX">
      <p>
        À l'issue de la qualification, CONFILEX peut proposer jusqu'à trois profils de spécialistes,
        sélectionnés selon des critères factuels et vérifiables :
      </p>
      <List items={[
        "la spécialité déclarée du professionnel",
        "la nature et la discipline du besoin identifié",
        "la zone géographique (proximité)",
        "la disponibilité signalée",
        "le niveau de référencement (référencé / non référencé)",
        "la cohérence du profil avec la demande (indice d'expérience vérifiable, langues parlées)",
      ]} />
      <p className="mt-3 italic text-slate-600">
        La proposition de profils ne constitue ni une garantie de résultat, ni une recommandation juridique
        engageante, ni une évaluation comparée de performance.
      </p>
    </Article>

    <Article n="3" title="Liberté de l'utilisateur">
      <p>L'utilisateur reste libre :</p>
      <List items={[
        "de contacter ou non l'un des profils proposés",
        "de choisir le spécialiste qu'il souhaite, parmi ou en dehors des profils suggérés",
        "de refuser l'ensemble des profils proposés",
        "de demander une nouvelle qualification s'il estime sa situation insuffisamment cernée",
      ]} />
    </Article>

    <Article n="4" title="Liberté du spécialiste">
      <p>Le spécialiste contacté reste libre :</p>
      <List items={[
        "d'accepter ou de refuser la prise en charge du dossier",
        "de fixer librement ses honoraires et les modalités de sa mission",
        "de demander toute information ou pièce complémentaire",
        "de refuser une mission en cas d'incompatibilité, conflit d'intérêts ou indisponibilité",
        "de mettre fin à la mission selon les règles déontologiques propres à sa profession",
      ]} />
    </Article>

    <Article n="5" title="Distinction spécialiste référencé / non référencé">
      <div className="grid md:grid-cols-2 gap-4 my-3">
        <div className="rounded-md border border-[#C9A961]/40 bg-[#FAF7F1] p-4">
          <p className="text-xs uppercase tracking-[0.18em] text-[#8A6E3A] mb-2">Spécialiste référencé CONFILEX</p>
          <List items={[
            "Profil créé ou validé par le professionnel lui-même",
            "Relation contractuelle active avec CONFILEX (abonnement)",
            "Badge « Spécialiste référencé » et cadre doré apposé sur le portrait",
            "Engagements déontologiques signés (Charte spécialiste référencé)",
          ]} />
        </div>
        <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
          <p className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Profil non référencé</p>
          <List items={[
            "Identifié à partir d'informations publiques disponibles",
            "Aucune relation contractuelle avec CONFILEX",
            "Mention « Profil non encore revendiqué / non partenaire CONFILEX »",
            "Le professionnel peut à tout moment revendiquer et compléter son profil",
          ]} />
        </div>
      </div>
    </Article>

    <Article n="6" title="Non-intervention dans la mission">
      <p>
        CONFILEX <strong>n'intervient pas</strong> dans la relation contractuelle finale entre l'utilisateur et le spécialiste choisi.
        La mission professionnelle relève exclusivement du spécialiste, de ses règles professionnelles et déontologiques,
        et de sa propre lettre de mission, convention d'honoraires, devis ou contrat.
      </p>
      <p className="mt-3">CONFILEX n'est pas responsable :</p>
      <List items={[
        "du contenu du conseil donné par le spécialiste",
        "des honoraires pratiqués par le spécialiste",
        "de l'issue du dossier ou de toute décision juridictionnelle",
        "de l'absence de réponse d'un spécialiste sollicité",
        "du refus de prise en charge d'un dossier par un spécialiste",
      ]} />
    </Article>

    <Article n="7" title="Consentement explicite avant transmission">
      <p>
        Avant toute transmission d'un dossier à un spécialiste, l'utilisateur doit confirmer expressément deux mentions :
      </p>
      <List items={[
        "J'accepte que les informations et documents transmis soient communiqués aux spécialistes sélectionnés afin de permettre l'étude de ma demande et une éventuelle prise de contact.",
        "Je comprends que CONFILEX est une plateforme d'intermédiation et ne fournit pas directement de consultation juridique, fiscale, comptable ou patrimoniale.",
      ]} />
      <p className="mt-3 italic text-slate-600">
        Sans ces deux acceptations, la transmission est bloquée.
      </p>
    </Article>
  </>
);

/* -------------------------------------------------------------------------- */
/* CHARTE EXPERT RÉFÉRENCÉ                                                     */
/* -------------------------------------------------------------------------- */
export const CharteExpertContent = () => (
  <>
    <h2 className="font-serif text-2xl text-[#0B3A2A] mb-6">Charte du spécialiste référencé CONFILEX</h2>
    <p className="text-slate-600 italic mb-8">
      Tout spécialiste référencé sur CONFILEX souscrit aux engagements suivants. Le non-respect peut entraîner
      la suspension ou le retrait du profil par CONFILEX.
    </p>

    <Article n="1" title="Légalité d'exercice">
      <List items={[
        "Exercer légalement et régulièrement la profession indiquée sur le profil",
        "Être inscrit auprès de l'ordre, compagnie ou organisme professionnel compétent lorsque cela est requis (CNB, Conseil supérieur du notariat, OEC, etc.)",
        "Maintenir à jour les informations d'inscription (numéro, barreau, ordre)",
        "Disposer des assurances professionnelles obligatoires",
      ]} />
    </Article>

    <Article n="2" title="Exactitude des informations publiées">
      <p>
        Le spécialiste est <strong>seul responsable</strong> de l'exactitude, de la mise à jour et de la licéité
        des informations publiées sur son profil (identité, spécialités, zone géographique, coordonnées,
        justificatifs professionnels, informations tarifaires éventuelles).
      </p>
      <p>
        CONFILEX peut contrôler, suspendre ou retirer un profil en cas d'information inexacte, trompeuse,
        ou non conforme à la réglementation applicable à la profession.
      </p>
    </Article>

    <Article n="3" title="Respect des règles professionnelles">
      <List items={[
        "Respecter strictement les règles déontologiques propres à la profession",
        "Vérifier l'absence de conflit d'intérêts avant toute prise en charge",
        "Respecter le secret professionnel lorsqu'il s'applique",
        "Établir une lettre de mission, convention d'honoraires ou devis conforme aux obligations applicables",
      ]} />
    </Article>

    <Article n="4" title="Confidentialité et données utilisateur">
      <List items={[
        "Traiter les données utilisateur reçues via CONFILEX de manière strictement confidentielle et sécurisée",
        "Ne pas utiliser ces données à d'autres fins que le traitement de la demande",
        "Respecter le Règlement Général sur la Protection des Données (RGPD)",
        "Détruire ou archiver les pièces transmises selon les règles de conservation propres à la profession",
      ]} />
    </Article>

    <Article n="5" title="Liberté de prise en charge">
      <p>
        Le spécialiste reste pleinement libre d'accepter ou de refuser une mission. Aucune obligation de prise en
        charge n'est imposée par CONFILEX. Le spécialiste fixe librement ses honoraires.
      </p>
    </Article>

    <Article n="6" title="Pas d'évaluation de performance par CONFILEX">
      <p>
        CONFILEX s'engage à ne jamais publier d'indicateur assimilable à un « taux de succès », « classement de
        performance » ou « palmarès qualitatif ». Seuls des critères factuels et déclaratifs vérifiables sont affichés.
      </p>
    </Article>
  </>
);

/* -------------------------------------------------------------------------- */
/* CHARTE UTILISATEUR                                                          */
/* -------------------------------------------------------------------------- */
export const CharteUtilisateurContent = () => (
  <>
    <h2 className="font-serif text-2xl text-[#0B3A2A] mb-6">Charte de l'utilisateur CONFILEX</h2>
    <p className="text-slate-600 italic mb-8">
      Tout utilisateur s'engage à respecter les principes suivants lorsqu'il utilise CONFILEX.
    </p>

    <Article n="1" title="Exactitude des informations transmises">
      <List items={[
        "Fournir des informations exactes, complètes et à jour dans le formulaire d'orientation",
        "Ne pas transmettre de documents frauduleux, falsifiés ou obtenus illicitement",
        "Ne pas usurper l'identité d'un tiers",
      ]} />
    </Article>

    <Article n="2" title="Usage loyal de la plateforme">
      <List items={[
        "Ne pas utiliser CONFILEX pour nuire à autrui, harceler ou diffamer",
        "Ne pas publier de contenus illicites, diffamatoires ou contraires aux bonnes mœurs",
        "Ne pas tenter de contourner les mécanismes techniques de sécurité ou de paiement",
        "Ne pas procéder au scraping ou à l'extraction massive de données du site",
      ]} />
    </Article>

    <Article n="3" title="Choix éclairé du spécialiste">
      <p>
        L'utilisateur reconnaît que les profils proposés par CONFILEX sont sélectionnés sur des critères
        factuels mais qu'il lui appartient, en dernier ressort, de vérifier l'adéquation du spécialiste à sa
        situation, de prendre contact avec lui et de conclure avec lui les engagements contractuels nécessaires
        (lettre de mission, convention d'honoraires, etc.).
      </p>
    </Article>

    <Article n="4" title="Compréhension du rôle de CONFILEX">
      <p>
        L'utilisateur reconnaît avoir compris que CONFILEX est une plateforme d'intermédiation et ne fournit
        pas directement de consultation juridique, fiscale, comptable ou patrimoniale. Les analyses
        automatiques produites par la plateforme ont une vocation strictement informative et préparatoire.
      </p>
    </Article>

    <Article n="5" title="Confidentialité réciproque">
      <p>
        Les échanges intervenant entre l'utilisateur et un spécialiste via CONFILEX sont confidentiels.
        L'utilisateur s'engage à ne pas divulguer publiquement les conseils, devis ou contenus échangés avec
        un spécialiste sans son accord explicite.
      </p>
    </Article>
  </>
);


const LegalContents = {
  MentionsContent,
  RolePlateformeContent,
  MiseEnRelationContent,
  DefinitionsContent,
  CGUContent,
  CGVContent,
  PrivacyContent,
  CookiesContent,
  CharteContent,
  CharteExpertContent,
  CharteUtilisateurContent,
};
export default LegalContents;

// Re-export from modular files (Phase A — cf. spec juridique du 10/02/2026).
export { DefinitionsContent };
